import { useEffect, useRef, type ComponentPropsWithoutRef } from "react";

export interface LoopingVideoProps
  extends Omit<ComponentPropsWithoutRef<"video">, "autoPlay" | "loop" | "playsInline" | "preload" | "muted"> {
  /** Off by default, as autoplay requires; a preview's sound button turns it on. */
  muted?: boolean;
}

/**
 * A video that plays as an animated cover, as live's `PreloadedMedia`: autoplaying, looped,
 * inline, muted unless told, its file loaded whole (`preload="auto"`). While the tab is hidden
 * it is paused, and it plays on when the tab is back (live's `useAutoplayWhileVisible`): a video
 * left playing in a hidden tab has its frames dropped and paints nothing on return.
 */
export function LoopingVideo({ muted = true, ...video }: LoopingVideoProps) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const onVisibility = () => {
      const node = ref.current;
      if (node === null) return;
      if (document.hidden) {
        node.pause();
        return;
      }
      // Refused only where playback is not allowed: the video stays as it is.
      node.play().catch(() => undefined);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);
  // React sets `muted` only when the element is made: a later change goes to the node.
  useEffect(() => {
    if (ref.current !== null) ref.current.muted = muted;
  }, [muted]);
  // A cover or an example has no words to caption.
  // oxlint-disable-next-line jsx-a11y/media-has-caption
  return <video ref={ref} autoPlay loop playsInline preload="auto" muted={muted} {...video} />;
}
